/** Helper com funcionalidades de manipulação de datas */

import {
  format,
  eachWeekOfInterval,
  isWithinInterval,
  compareAsc,
  parseISO
} from 'date-fns' // https://github.com/date-fns/date-fns
import pt from 'date-fns/locale/pt-BR'

/** Array com os dias da semana */
const weekdays = new Array(
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado'
)

/**
 * Retorna um array com os dias da semana
 * @returns {Array} dias da semana
 */
export const getWeekDays = () => weekdays

/** Array com os meses do ano */
const months = new Array(
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
)

/**
 * Retorna um array com os meses do ano
 * @returns {Array} Meses do ano
 */
export const getYearMonths = () => months

/**
 * Formata uma data de acordo com a string passada
 * @param {Date} date Date obj da data a ser formatada
 * @param {String} formatStr String com o formato final da data
 * @param {Boolean} parse Se deve parsear o ISO antes de formatar
 * @returns {String}
 */
export const formatDate = (date, formatStr = 'dd/MM/yyyy', parse = false) => {
  if (parse) {
    return format(parseISO(date), formatStr, { locale: pt })
  }
  return format(date, formatStr, { locale: pt })
}
export const formatDateHour = (
  date,
  formatStr = 'dd/MM/yyyy hh:mm:ss',
  parse = false
) => {
  if (parse) {
    return format(parseISO(date), formatStr, { locale: pt })
  }
  return format(date, formatStr, { locale: pt })
}

/**
 * Retorna um array com as datas dos dias da semana dentro de um dado intervalo
 * Obs: O plugin considera 0 como domingo
 * @param {Object} interval - { start: Date, end: Date } - Intervalo de datas que deve ser retornado
 * @param {Array} days - [Number] -  Array com os dias da semana que deve ser retornados
 * @returns {Array}
 */
export const getWeekDaysFromInterval = (interval, days) => {
  let dates = []

  days.forEach((day) => {
    const result = eachWeekOfInterval(interval, { weekStartsOn: day })

    dates = [...dates, ...result]
  })

  /** Retorna apenas as datas de dentro do intervalo */
  return dates.filter((resDate) => isWithinInterval(resDate, interval))
}

/**
 * Compara duas datas no formato ISO
 * @param {String} dateLeft
 * @param {String} dateRight
 *
 * @returns {Number} - Retorna 1 se a primeira data é maior, -1 se a primeira data é menor e 0 se são iguais
 *
 * @example compareIsoDates('2021-06-18', '2021-07-18') >>>> -1
 */
export const compareIsoDates = (dateLeft, dateRight) => {
  return compareAsc(parseISO(dateLeft), parseISO(dateRight))
}

/**
 * Compara o horário em minutos de duas datas
 * @param {Data} dateLeft
 * @param {Date} dateRight
 *
 * @returns {Number} - Retorna o valor em minutos
 */
export const differenceInMinutes = (dateLeft, dateRight) => {
  const differenceInMilliseconds = dateLeft.getTime() - dateRight.getTime()
  const differenceInMinutes = differenceInMilliseconds / (1000 * 60)

  return differenceInMinutes
}

/**
 * Retorna a hora e o dia formatados
 * @param {String} dateTime - String com hora e dia para ser formatado
 *
 * @returns {Object} - Retorna a data e a hora formata
 *
 * @example extractDateTime('2023-05-29T17:33:32.387')
 */
export const extractDateTime = (dateTime) => {
  const [date, time] = dateTime.split('T')
  const formattedDate = formatDate(date, 'dd/MM/yyyy', true)
  const formattedTime = time.split('.')[0]
  return [formattedDate, formattedTime]
}

/**
 * Retorna apenas as horas e os minutos de uma data
 * @param {string} fullDate - data com a hora
 * @returns horas no formato 00:00
 */
export const getDateHours = (fullDate) => {
  const date = new Date(fullDate)

  const hours = date.getHours().toLocaleString('pt-BR', {
    minimumIntegerDigits: 2
  })
  const minutes = date.getMinutes().toLocaleString('pt-BR', {
    minimumIntegerDigits: 2
  })

  return `${hours}:${minutes}`
}

/**
 * Formata uma data no padrão ISO-8601
 * @param {dateTime} date Data a ser formatada
 * @param {boolean} setZeroHours se deve setar o horário como 0
 * @returns string da data formatada
 */
export const dateToISO = (date, setZeroHours) => {
  if (setZeroHours) {
    return format(new Date(date), `yyyy-MM-dd'${'T'}'00:00:00`)
  }

  return format(new Date(date), `yyyy-MM-dd'${'T'}'HH:mm:ssXX`)
}
